import Link from 'next/link'
import type { MouseEvent } from 'react'
import { navLinkToHref } from '@/lib/navigation'
import { OPENS_IN_NEW_TAB_HINT } from '@/lib/a11y'
import type { TopBannerType } from '@/lib/types/global-content'
import { isHyTourNavLink, withHyTourClassWhen } from '@/lib/hyTour'

type TopBannerProps = {
  data: TopBannerType | null
  onDismiss?: () => void
  onSectionLinkClick?: (event: MouseEvent<HTMLAnchorElement>, href: string) => void
  scheduleTourUrl?: string | null
}

const DEFAULT_BANNER_BG = '#1E1E1E'

function BannerLink({
  link,
  className,
  onSectionLinkClick,
  scheduleTourUrl = null,
}: {
  link: NonNullable<NonNullable<TopBannerType>['link']>
  className?: string
  onSectionLinkClick?: (event: MouseEvent<HTMLAnchorElement>, href: string) => void
  scheduleTourUrl?: string | null
}) {
  if (!link.label) return null
  const isTour = isHyTourNavLink(link, scheduleTourUrl)
  const isExternal = !isTour && link.linkType === 'ExternalLink'
  const href = navLinkToHref(link, { scheduleTourUrl })
  const classes = withHyTourClassWhen(className ?? '', isTour)
  if (isTour) {
    return (
      <a href="#" className={classes}>
        {link.label}
      </a>
    )
  }
  return (
    <Link
      href={href}
      className={classes}
      onClick={(event) => onSectionLinkClick?.(event, href)}
      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {link.label}
      {isExternal ? (
        <span className="sr-only">{OPENS_IN_NEW_TAB_HINT}</span>
      ) : null}
    </Link>
  )
}

function MobileBannerCopy({
  title,
  link,
  onSectionLinkClick,
  scheduleTourUrl = null,
}: {
  title: string
  link: NonNullable<TopBannerType>['link']
  onSectionLinkClick?: (event: MouseEvent<HTMLAnchorElement>, href: string) => void
  scheduleTourUrl?: string | null
}) {
  return (
    <p className="flex shrink-0 items-center gap-2 whitespace-nowrap font-body text-xs font-normal leading-4 text-white">
      <span>{title}</span>
      {link?.label && (
        <BannerLink
          link={link}
          className="underline underline-offset-2 transition-opacity hover:opacity-80"
          onSectionLinkClick={onSectionLinkClick}
          scheduleTourUrl={scheduleTourUrl}
        />
      )}
    </p>
  )
}

function DismissButton({
  onDismiss,
  backgroundColor,
}: {
  onDismiss: () => void
  backgroundColor: string
}) {
  return (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss announcement"
      className="touch-target absolute top-1/2 right-2 z-[2] flex size-[22px] shrink-0 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-white transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 md:right-3"
      style={{ backgroundColor }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M1 1l10 10M11 1 1 11"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </button>
  )
}

export default function TopBanner({
  data,
  onDismiss,
  onSectionLinkClick,
  scheduleTourUrl = null,
}: TopBannerProps) {
  if (!data || !data.title) return null

  const { title, link, backgroundColor } = data
  const bg = backgroundColor?.trim() || DEFAULT_BANNER_BG

  return (
    <div
      data-fig="368:2261"
      role="region"
      aria-label="Announcement"
      className="relative isolate z-[15] flex h-[var(--height-specials-bar)] w-full shrink-0 items-center justify-center px-3 py-2 md:px-4 md:py-2.5"
      style={{ backgroundColor: bg }}
    >
      {/* Mobile: marquee + edge fade — reserve space so copy never sits under close */}
      <div className="relative min-h-4 min-w-0 flex-1 overflow-hidden pr-10 md:hidden">
        <div className="flex h-full w-max items-center animate-banner-marquee hover:[animation-play-state:paused] motion-reduce:animate-none">
          <MobileBannerCopy
            title={title}
            link={link}
            onSectionLinkClick={onSectionLinkClick}
            scheduleTourUrl={scheduleTourUrl}
          />
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-12"
          style={{
            background: `linear-gradient(270deg, ${bg} 40%, transparent 100%)`,
          }}
          aria-hidden="true"
        />
      </div>

      {/* Desktop: centered copy */}
      <div className="hidden min-w-0 flex-1 items-center justify-center px-8 md:flex">
        <p className="text-center font-body text-sm font-normal leading-5 text-white">
          {title}
          {link?.label && (
            <>
              {' '}
              <BannerLink
                link={link}
                className="underline underline-offset-2 transition-opacity hover:opacity-80"
                onSectionLinkClick={onSectionLinkClick}
                scheduleTourUrl={scheduleTourUrl}
              />
            </>
          )}
        </p>
      </div>

      {onDismiss && (
        <DismissButton onDismiss={onDismiss} backgroundColor={bg} />
      )}
    </div>
  )
}
